import type {SnapshotItem, SnapshotResult} from './snapshotStore.types';
import type {Ref} from 'vue';
import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {SnapshotStatus} from './snapshotStore.types';

export const createSnapshotStore = <T extends SnapshotItem<unknown>>(storeName: string) =>
  defineStore(`snapshot:${storeName}`, () => {
    const items = ref([]) as Ref<T[]>;

    const finishedCount = computed(() => items.value.filter((item) => item.status !== SnapshotStatus.Pending).length);
    const hasErrors = computed(() => items.value.some((item) => item.status === SnapshotStatus.Error));
    const isFinished = computed(() => items.value.length > 0 && finishedCount.value >= items.value.length);

    const getItemById = (id: string): T | undefined => {
      return items.value.find((item) => item.id === id);
    };

    const upsertItems = (newItems: T[]) => {
      items.value = Array.from(new Map([...items.value, ...newItems].map((item) => [item.id, item])).values());
    };

    const updateItem = (updatedItem: T) => {
      const index = items.value.findIndex((item) => item.id === updatedItem.id);

      if (index !== -1) {
        items.value[index] = updatedItem;
      }
    };

    const updateItemAfterProp = (id: string, after: unknown) => {
      const item = getItemById(id);

      if (!item) {
        return;
      }

      const updatedItem = {...item, after} as T;

      updateItem(updatedItem);
    };

    const finishItem = ({
      id,
      success,
      error,
      after,
    }: SnapshotResult & {id: string; after?: T['after']}): T | undefined => {
      const item = getItemById(id);

      if (!item || item.status !== SnapshotStatus.Pending) {
        return undefined;
      }

      const itemSucceeded = {...item, status: SnapshotStatus.Success, after: after ?? item.after} as T;
      const itemFailed = {...item, status: SnapshotStatus.Error, error, after: after ?? item.after} as T;

      const finishedItem = success ? itemSucceeded : itemFailed;

      updateItem(finishedItem);

      return finishedItem;
    };

    const reset = () => {
      items.value = [];
    };

    return {
      items,
      finishedCount,
      hasErrors,
      isFinished,
      getItemById,
      updateItemAfterProp,
      upsertItems,
      finishItem,
      updateItem,
      reset,
    };
  });
